import { Controller } from "@hotwired/stimulus"

let apiPromise

function loadApi(siteKey) {
  if (window.grecaptcha?.execute) return Promise.resolve(window.grecaptcha)
  if (apiPromise) return apiPromise

  apiPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    const timer = setTimeout(() => fail(), 10000)
    const fail = () => {
      clearTimeout(timer)
      script.remove()
      apiPromise = undefined
      reject(new Error("reCAPTCHA could not load"))
    }
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.onerror = fail
    script.onload = () => {
      if (!window.grecaptcha) return fail()
      window.grecaptcha.ready(() => {
        clearTimeout(timer)
        resolve(window.grecaptcha)
      })
    }
    document.head.appendChild(script)
  })
  return apiPromise
}

export default class extends Controller {
  static targets = ["token", "error", "submit"]
  static values = { siteKey: String, action: String }

  connect() {
    this.reset()
    // 読み込みを開始し、トークンは送信の直前に取得する。
    if (this.siteKeyValue) loadApi(this.siteKeyValue).catch(() => {})
  }

  disconnect() {
    this.attempt = null
    clearTimeout(this.timer)
  }

  async submit(event) {
    if (this.authorized) {
      this.authorized = false
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.busy) return

    this.busy = true
    this.submitTarget.disabled = true
    this.errorTarget.hidden = true
    const attempt = this.attempt = Symbol()

    try {
      if (!this.siteKeyValue) throw new Error("Missing site key")
      const token = await Promise.race([
        loadApi(this.siteKeyValue).then(api => api.execute(this.siteKeyValue, { action: this.actionValue })),
        new Promise((_, reject) => {
          this.timer = setTimeout(() => reject(new Error("reCAPTCHA timed out")), 15000)
        })
      ])
      if (this.attempt !== attempt || !this.element.isConnected) return
      if (!token) throw new Error("Missing token")

      this.tokenTarget.value = token
      this.authorized = true
      this.submitTarget.disabled = false
      this.element.requestSubmit(event.submitter || this.submitTarget)
      // 入力検証で送信イベントが発生しなかった場合は再試行できるようにする。
      if (this.authorized) this.reset()
    } catch {
      if (this.attempt !== attempt || !this.element.isConnected) return
      this.reset()
      this.errorTarget.textContent = "認証を確認できませんでした。通信環境を確認し、もう一度送信してください。"
      this.errorTarget.hidden = false
    } finally {
      clearTimeout(this.timer)
    }
  }

  reset() {
    this.tokenTarget.value = ""
    this.authorized = false
    this.busy = false
    this.submitTarget.disabled = false
  }
}
